import React, { useEffect, useRef, useState } from "react";
import {
  GoBackNavWrapper,
  MasonryImg,
  MasonryItem,
  MasonryWrapper,
  NavigationBar,
  PropertyTitle,
  ShareWrapper,
  Wrapper,
  AIBadge,
  MasonryImgWrapper
} from "./styles";
import { PropertyPhotos } from "../../global/types";
import { useHistory, useLocation, useParams } from "react-router-dom";
import api from "../../services/api";
import { HiOutlineChevronLeft } from "react-icons/hi";
import { FiShare2 } from "react-icons/fi";
import PhotosModal from "./components/PhotosModal";
import PhotoGallerySkeleton from "./components/PhotoGallerySkeleton";

interface ILocation {
  photos: PropertyPhotos[];
  title: string;
}

interface IParams {
  id: string;
}

interface IModal {
  open: () => void;
  close: () => void;
}

function getBreakpoint(): "mobile" | "tablet" | "desktop" {
  if (window.innerWidth < 360) return "mobile";
  if (window.innerWidth < 724) return "tablet";
  return "desktop";
}

function PhotoGallery() {
  const { state } = useLocation<ILocation>();
  const { id } = useParams<IParams>();
  const history = useHistory();
  const dialog = useRef<IModal>(null);

  const [startIndex, setStartIndex] = useState(0);
  const [propertyDetails, setPropertyDetails] = useState<ILocation | null>(null);
  const [loading, setLoading] = useState(true);
  const [breakpoints, setBreakpoints] = useState<"mobile" | "tablet" | "desktop">(getBreakpoint());

  useEffect(() => {
    async function fetchProperty() {
      try {
        setLoading(true);
        const response = await api.get(`/api/client/properties/${parseInt(id)}`);
        const responseData = response.data.data;
        const { photos, title } = responseData;

        setPropertyDetails({ photos, title });
      } catch (error) {
        history.push("/404");
      } finally {
        setLoading(false);
      }
    }

    if (state?.photos && state?.title) {
      setPropertyDetails({ photos: state.photos, title: state.title });
      setLoading(false);
    } else {
      fetchProperty();
    }
  }, [id, history, state]);

  useEffect(() => {
    function handleResize() {
      setBreakpoints(getBreakpoint());
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const NUM_OF_COLS = breakpoints === "mobile" ? 1 : breakpoints === "tablet" ? 2 : 4;

  let formattedPhotos: PropertyPhotos[][] = new Array(NUM_OF_COLS)
    .fill(null)
    .map(() => []);

  if (propertyDetails?.photos && Array.isArray(propertyDetails.photos)) {
    propertyDetails.photos.forEach((photo, index) => {
      formattedPhotos[index % NUM_OF_COLS].push(photo);
    });
  }

  function handleGoBack() {
    if (state?.photos && state?.title) {
      history.goBack();
    } else {
      history.push(`/imovel/${id}`);
    }
  }

  function handleOpenModal(photoIndex: number) {
    setStartIndex(photoIndex);
    dialog.current?.open();
  }

  function handleCloseModal() {
    dialog.current?.close();
  }

  if (loading) {
    return <PhotoGallerySkeleton />;
  }

  if (!propertyDetails) {
    return (
      <Wrapper>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2> Erro ao carregar</h2>
          <p>Não foi possível carregar os dados do imóvel</p>
          <button onClick={() => window.location.reload()}>Tentar Novamente</button>
        </div>
      </Wrapper>
    );
  }

  if (!propertyDetails.photos || propertyDetails.photos.length === 0) {
    return (
      <Wrapper>
        <NavigationBar>
          <GoBackNavWrapper onClick={handleGoBack}>
            <HiOutlineChevronLeft />
            <PropertyTitle>{propertyDetails.title || 'Voltar'}</PropertyTitle>
          </GoBackNavWrapper>
        </NavigationBar>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2> Nenhuma Foto Disponível</h2>
          <p>Este imóvel não possui fotos cadastradas.</p>
        </div>
      </Wrapper>
    );
  }

  return (
    <>
      <PhotosModal
        ref={dialog}
        photos={propertyDetails.photos}
        startIndex={startIndex}
        onClose={handleCloseModal}
      />
      <Wrapper>
        <NavigationBar>
          <GoBackNavWrapper onClick={handleGoBack}>
            <HiOutlineChevronLeft />
            <PropertyTitle>{propertyDetails.title}</PropertyTitle>
          </GoBackNavWrapper>
          <ShareWrapper
            href={`https://api.whatsapp.com/send?text=${window.location.origin}/imovel/${id}/photos`}
            target="_blank"
          >
            <FiShare2 />
            <p>Compartilhar</p>
          </ShareWrapper>
        </NavigationBar>
        <MasonryWrapper>
          {formattedPhotos.map((photoColumn, index) => (
            <MasonryItem key={index}>
            {photoColumn.map((photo, photoIndex) => (
              <MasonryImgWrapper
                key={photo.id}
                onClick={() =>
                  handleOpenModal(index + photoIndex * NUM_OF_COLS)
                }
              >
                {(photo.generated_by_ai === 1 || photo.generated_by_ai === true) && (
                  <AIBadge>Gerada por IA</AIBadge>
                )}

                <MasonryImg
                  src={photo.url_l}
                  alt={`Foto ${index + photoIndex * NUM_OF_COLS + 1}`}
                />
              </MasonryImgWrapper>
            ))}
            </MasonryItem>
          ))}
        </MasonryWrapper>
      </Wrapper>
    </>
  );
}

export default PhotoGallery;